import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Select from 'react-select';
import { getAPIConfig } from '@tcp/core/src/utils';
import { getQueryUrlWithPage } from '@tcp/core/src/components/features/browse/CategoryListingPage/container/CategoryListingPage.utils';
import {
  PaginationWrapper,
  PaginationDropDown,
  PaginatorButton,
  PaginatorAnchor,
} from './PlpPaginator.styles';

const getPageStr = (pageNumber) => (pageNumber > 1 ? `?page=${pageNumber}` : '');

const getHref = ({ pageNumber, pathName }) => {
  if (!pathName) {
    return pathName;
  }

  const catUrl = pathName.indexOf('?') > 1 ? pathName.split('?')[0] : pathName.split('#')[0];

  const pageStr = getPageStr(pageNumber);

  const { reqHostname: hostname } = getAPIConfig();
  const BASE_URL = `https://${hostname}`;

  return `${BASE_URL}${catUrl}${pageStr}`;
};

const getAsPath = ({ pageNumber, pathName }) => {
  const pathNameWithoutCountrySlug = `/c/${pathName?.split('/c/')[1]}`;
  const [pathNameWithoutQuery, urlQueryValues] = pathNameWithoutCountrySlug.split('?');
  return urlQueryValues
    ? `${pathNameWithoutQuery}?${getQueryUrlWithPage(urlQueryValues, pageNumber)}`
    : `${pathNameWithoutQuery}?page=${pageNumber}`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Paginator = (props) => {
  const {
    currentPage: selectedPage = 1,
    totalCount,
    pageSize,
    onPageChange,
    trackClick,
    isNewNavigationImagesEnabled,
    processedBreadCrumbs,
    isFacetedNavigationNoIndexApplicableCheck,
    pathName,
    cid,
    setUpdatedUrlActn,
  } = props;
  // compute the total number of pages
  const totalPages = Math.ceil(totalCount / pageSize);

  const [pageNumber, setPageNumber] = useState(selectedPage);
  const [isDisabled, setIsDisabled] = useState(totalPages < 2);

  // customize the react-select component styling to match designs
  const customStyles = {
    singleValue: (provided) => {
      const color = isDisabled && 'hsl(0, 0%, 80%)';
      return { ...provided, color };
    },
    option: (provided, state) => {
      const backgroundColor = state.isSelected ? '#ebebeb' : '';
      const color = '#00000';
      return { ...provided, color, backgroundColor };
    },
    menu: (provided) => {
      const boxShadow = '0 0 0 1px hsla(0, 0%, 0%, 1), 0 4px 11px hsla(0, 0%, 0%, 0.1)';
      return { ...provided, boxShadow };
    },
    indicatorSeparator: (provided) => {
      const display = 'none';
      return { ...provided, display };
    },
    dropdownIndicator: (provided) => {
      const color = isDisabled ? 'hsl(0, 0%, 80%)' : 'hsl(0, 0%, 0%)';
      return { ...provided, color };
    },
    control: (provided, state) => {
      const border = 'none';
      const backgroundColor = state.isFocused ? '#ebebeb' : '';
      return { ...provided, border, backgroundColor };
    },
  };

  useEffect(() => {
    if (pageNumber !== selectedPage) {
      setPageNumber(selectedPage);
    }
    setIsDisabled(totalPages < 2);
  }, [selectedPage, totalPages]);

  const getPageNavigationText = () => {
    const breadCrumbNames = processedBreadCrumbs?.map((breadCrumb) =>
      breadCrumb?.displayName?.toLowerCase()
    );
    if (isNewNavigationImagesEnabled) {
      return `topmenu-withimage-${breadCrumbNames?.join('-')}`;
    }
    return `topmenu-${breadCrumbNames?.join('-')}`;
  };

  const analyticsOnClick = (type) => {
    const pageNavigationText = getPageNavigationText();
    trackClick({
      plp_pagination_cd153: `${pageNavigationText}|${type}`,
    });
  };

  const updatePage = ({ value: page, skipUrlUpdate }) => {
    setPageNumber(page);
    setTimeout(() => {
      onPageChange(page, skipUrlUpdate);
    }, 10);
  };

  const prevAsPath = getAsPath({
    pageNumber: pageNumber > 1 ? pageNumber - 1 : pageNumber,
    pathName,
  });
  const nextAsPath = getAsPath({
    pageNumber: pageNumber < totalPages ? pageNumber + 1 : pageNumber,
    pathName,
  });

  const increment = (event) => {
    setTimeout(() => {
      if (pageNumber + 1 <= totalPages) {
        analyticsOnClick('next');
        updatePage({ value: pageNumber + 1, skipUrlUpdate: true });
        setUpdatedUrlActn({ updatedUrl: nextAsPath });
      }
    }, 0);
    event.target.blur();
  };

  const decrement = (event) => {
    setTimeout(() => {
      if (pageNumber - 1 >= 1) {
        analyticsOnClick('previous');
        updatePage({ value: pageNumber - 1, skipUrlUpdate: true });
        setUpdatedUrlActn({ updatedUrl: prevAsPath });
      }
    }, 0);
    event.target.blur();
  };

  const optionsArray = [];

  for (let index = 1; index <= totalPages; index += 1) {
    optionsArray.push({ value: index, label: `Page ${index}` });
  }
  const prevButtonDisabled = pageNumber === 1 || isDisabled;
  const nextButtonDisabled = pageNumber === totalPages || isDisabled;
  const prevHref = getHref({ pageNumber: pageNumber > 1 ? pageNumber - 1 : pageNumber, pathName });
  const nextHref = getHref({
    pageNumber: pageNumber < totalPages ? pageNumber + 1 : pageNumber,
    pathName,
  });
  const toPath = `/c?cid=${cid}`;

  return (
    <PaginationWrapper>
      {prevButtonDisabled ? (
        <PaginatorButton className="prev" onClick={decrement} disabled={prevButtonDisabled}>
          Previous
        </PaginatorButton>
      ) : (
        <PaginatorAnchor className="prev" to={toPath} asPath={prevAsPath} onClick={decrement}>
          Previous
        </PaginatorAnchor>
      )}

      <PaginationDropDown>
        <Select
          data-testid="page-select"
          isDisabled={isDisabled}
          blurInputOnSelect={true}
          value={{ value: pageNumber, label: `Page ${pageNumber} of ${totalPages}` }}
          options={optionsArray}
          placeholder={`Page ${pageNumber} of ${totalPages}`}
          onChange={(value) => {
            updatePage(value);
          }}
          styles={customStyles}
          inputProps={{ readOnly: true }}
          isSearchable={false}
        />
      </PaginationDropDown>
      {nextButtonDisabled ? (
        <PaginatorButton className="next" onClick={increment} disabled={nextButtonDisabled}>
          Next
        </PaginatorButton>
      ) : (
        <PaginatorAnchor className="next" to={toPath} asPath={nextAsPath} onClick={increment}>
          Next
        </PaginatorAnchor>
      )}
      {!prevButtonDisabled && !isFacetedNavigationNoIndexApplicableCheck ? (
        <Head>
          <link rel="prev" href={prevHref} />
        </Head>
      ) : null}
      {!nextButtonDisabled && !isFacetedNavigationNoIndexApplicableCheck ? (
        <Head>
          <link rel="next" href={nextHref} />
        </Head>
      ) : null}
    </PaginationWrapper>
  );
};

export default Paginator;
